import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useToast } from '../../../Hooks/ToasContext'
import { useAuth } from '../../../Hooks/Auth'
import { GetHCPFlowKanbanUseCase } from '../../../Usecase/HCP/Flows/get-hcp-flow-kanban-usecase'
import { MoveHCPFlowCardUseCase } from '../../../Usecase/HCP/Flows/move-hcp-flow-card-usecase'
import {
  applyCardMove,
  getMoveTargetOrderProductIds,
  patchMovedCardAfterApi,
} from '../../../Services/HCP/flows/hcp-flow-drag.helpers'
import { getLatestFlowHistoryActor } from '../../../Services/HCP/flows/hcp-flow-history.helpers'
import type { IFlowCardMovePayload } from '../../../Services/HCP/flows/hcp-flow-drag.helpers'
import {
  appendColumnPage,
  replaceKanbanColumns,
  getNextColumnPage,
} from '../../../Services/HCP/flows/hcp-flow-pagination.helpers'
import {
  HCPFlowEnum,
  IHCPFlowKanbanColumn,
  IHCPFlowKanbanResponse,
} from '../../../Services/HCP/flows/hcp-flow.interface'
import { HCPFlowService } from '../../../Services/HCP/flows/hcp-flow.service'
import { getHCPFlowErrorMessage } from '../../../Services/HCP/flows/hcp-flow-error.helpers'

export interface IFlowTabConfig {
  flow: HCPFlowEnum
  label: string
  enabled: boolean
}

export const FLOW_TABS: IFlowTabConfig[] = [
  { flow: HCPFlowEnum.AWAITING_ART, label: 'Fluxo 1 — Arte Final', enabled: true },
  { flow: HCPFlowEnum.SHEET, label: 'Fluxo 2 — Montagem de Cartelas', enabled: true },
  { flow: HCPFlowEnum.PRODUCTION, label: 'Fluxo 3 — OS e Produção', enabled: true },
  { flow: HCPFlowEnum.MACHINE_PRODUCTION, label: 'Fluxo 4 — Produção de Máquinas', enabled: false },
  { flow: HCPFlowEnum.LOGISTICS, label: 'Fluxo 5 — Logística', enabled: true },
]

export type FiltroArteAprovadaType = 'todos' | 'aprovada' | 'pendente'

interface IUseFlowManagementProps {
  getHCPFlowKanbanUseCase: GetHCPFlowKanbanUseCase
  moveHCPFlowCardUseCase: MoveHCPFlowCardUseCase
}

export type { IFlowCardMovePayload } from '../../../Services/HCP/flows/hcp-flow-drag.helpers'

const PAGE_SIZE = 50
const SEARCH_DEBOUNCE_MS = 400

function parseFlowParam(value: string | null): HCPFlowEnum {
  const match = FLOW_TABS.find((tab) => tab.flow === value)
  if (match?.enabled) return match.flow
  return HCPFlowEnum.AWAITING_ART
}

function getAllArtsApprovedParam(
  filtro: FiltroArteAprovadaType
): boolean | undefined {
  if (filtro === 'aprovada') return true
  if (filtro === 'pendente') return false
  return undefined
}

export function useFlowManagement({
  getHCPFlowKanbanUseCase,
  moveHCPFlowCardUseCase,
}: IUseFlowManagementProps) {
  const { showToast } = useToast()
  const { user } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()

  const activeFlow = useMemo(
    () => parseFlowParam(searchParams.get('flow')),
    [searchParams]
  )

  const [columns, setColumns] = useState<IHCPFlowKanbanColumn[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingMoreColumnId, setLoadingMoreColumnId] = useState<string | null>(null)
  const [movingCardId, setMovingCardId] = useState<string | null>(null)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)

  const [filtroBusca, setFiltroBusca] = useState(searchParams.get('search') ?? '')
  const [filtroArte, setFiltroArte] = useState<FiltroArteAprovadaType>(
    (searchParams.get('arte') as FiltroArteAprovadaType) || 'todos'
  )

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const requestIdRef = useRef(0)
  const columnsRef = useRef(columns)
  columnsRef.current = columns

  const syncUrlParams = useCallback((flow: HCPFlowEnum, search: string, arte: FiltroArteAprovadaType) => {
    const params = new URLSearchParams()
    if (flow !== HCPFlowEnum.AWAITING_ART) params.set('flow', flow)
    if (search.trim()) params.set('search', search.trim())
    if (arte !== 'todos') params.set('arte', arte)
    setSearchParams(params, { replace: true })
  }, [setSearchParams])

  const fetchKanban = useCallback(async (options?: {
    page?: number
    append?: boolean
    flow?: HCPFlowEnum
    search?: string
    arte?: FiltroArteAprovadaType
    columnId?: string
  }) => {
    const flow = options?.flow ?? activeFlow
    if (!HCPFlowService.isFlowEnabled(flow)) return

    const targetColumnId = options?.columnId
    const append = Boolean(options?.append && targetColumnId)
    const page = options?.page ?? 1
    const search = options?.search ?? filtroBusca
    const arte = options?.arte ?? filtroArte
    const requestId = ++requestIdRef.current

    if (append && targetColumnId) {
      setLoadingMoreColumnId(targetColumnId)
    } else {
      setLoading(true)
    }

    try {
      const result = await getHCPFlowKanbanUseCase.handle({
        flow,
        page,
        offset: PAGE_SIZE,
        searchBy: search.trim() || undefined,
        allArtsApproved: flow === HCPFlowEnum.LOGISTICS
          ? undefined
          : getAllArtsApprovedParam(arte),
      })

      if (requestId !== requestIdRef.current) return

      if (result.isFailure) {
        const message =
          (result.error as any)?.error ??
          (result.error as any)?.message ??
          'Erro ao carregar o Kanban de fluxo.'
        showToast(String(message), 'error')
        return
      }

      const data = result.getValue() as IHCPFlowKanbanResponse
      setColumns((prev) => (
        append && targetColumnId
          ? appendColumnPage(prev, data.columns, targetColumnId)
          : replaceKanbanColumns(data.columns)
      ))
      setLastUpdated(new Date())
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false)
        setLoadingMoreColumnId(null)
      }
    }
  }, [activeFlow, filtroArte, filtroBusca, getHCPFlowKanbanUseCase, showToast])

  const handleTabChange = (flow: HCPFlowEnum) => {
    const tab = FLOW_TABS.find((item) => item.flow === flow)
    if (!tab?.enabled) return
    syncUrlParams(flow, filtroBusca, filtroArte)
  }

  const handleRefresh = () => {
    setColumns([])
    fetchKanban({ page: 1, append: false })
  }

  const handleLoadMoreColumn = (column: IHCPFlowKanbanColumn) => {
    if (!column.hasNextPage || loadingMoreColumnId) return

    fetchKanban({
      page: getNextColumnPage(column),
      append: true,
      columnId: column.statusId,
    })
  }

  const handleCardMove = useCallback(async (payload: IFlowCardMovePayload) => {
    const { card, sourceStatusId, destStatusId, destIndex, destSequence } = payload

    if (sourceStatusId === destStatusId) return

    const destColumn = columnsRef.current.find((column) => column.statusId === destStatusId)
    if (!destColumn) return

    const snapshot = columnsRef.current
    const cardIdStr = String(card.id)
    const actorUsername = user?.username ?? null

    setMovingCardId(cardIdStr)
    setColumns((prev) => applyCardMove(
      prev,
      sourceStatusId,
      destStatusId,
      card.id,
      destIndex,
      destSequence,
      {
        name: destColumn.name,
        displayCode: destColumn.displayCode,
      },
      actorUsername
    ))

    try {
      const result = await moveHCPFlowCardUseCase.handle({
        flow: activeFlow,
        statusSequence: destSequence,
        orderProductIds: getMoveTargetOrderProductIds(card),
      })

      if (result.isFailure) {
        setColumns(snapshot)
        showToast(
          getHCPFlowErrorMessage(result.error, 'Não foi possível mover o card. Tente novamente.'),
          'error'
        )
        return
      }

      const updatedCards = result.getValue() ?? []
      setColumns((prev) => patchMovedCardAfterApi(
        prev,
        card.id,
        destStatusId,
        updatedCards,
        actorUsername
      ))

      const actorLabel = getLatestFlowHistoryActor(updatedCards[0]?.hcpFlowHistories) ?? actorUsername
      const successMessage = actorLabel
        ? `Movido para "${destColumn.name}" por ${actorLabel}.`
        : `Movido para "${destColumn.name}".`
      showToast(successMessage, 'success')

      setLastUpdated(new Date())
    } finally {
      setMovingCardId((current) => (current === cardIdStr ? null : current))
    }
  }, [activeFlow, moveHCPFlowCardUseCase, showToast, user?.username])

  const filtersInitializedRef = useRef(false)

  useEffect(() => {
    setColumns([])
    fetchKanban({ page: 1, append: false, flow: activeFlow })
  }, [activeFlow])

  useEffect(() => {
    if (!filtersInitializedRef.current) {
      filtersInitializedRef.current = true
      return
    }

    if (debounceRef.current) clearTimeout(debounceRef.current)

    debounceRef.current = setTimeout(() => {
      syncUrlParams(activeFlow, filtroBusca, filtroArte)
      setColumns([])
      fetchKanban({ page: 1, append: false, search: filtroBusca, arte: filtroArte })
    }, SEARCH_DEBOUNCE_MS)

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [filtroBusca, filtroArte])

  const lastUpdatedLabel = lastUpdated
    ? format(lastUpdated, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })
    : null

  return {
    activeFlow,
    flowTabs: FLOW_TABS,
    columns,
    loading,
    loadingMoreColumnId,
    lastUpdatedLabel,
    filtroBusca,
    setFiltroBusca,
    filtroArte,
    setFiltroArte,
    handleTabChange,
    handleRefresh,
    handleLoadMoreColumn,
    handleCardMove,
    movingCardId,
  }
}
